import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { CasesCopy } from './content'

/**
 * Interface mock-ups for the case studies, redrawn from scratch in the site's visual language.
 * Each mock is laid out at a fixed design size and scaled to its container, so it stays crisp
 * and proportional from phones to wide screens. All data inside is illustrative.
 */

type Ui = CasesCopy['ui']

function useScale(designWidth: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => setScale(Math.min(entry.contentRect.width / designWidth, 1.25)))
    observer.observe(element)
    return () => observer.disconnect()
  }, [designWidth])
  return { ref, scale }
}

/** Plays a mock's entrance sequence once it scrolls into view. */
function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true)
        observer.disconnect()
      }
    }, { threshold: 0.25 })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return { ref, inView }
}

export function MockFrame({ path, width = 760, height, tone = 'primary', children }: { path: string; width?: number; height: number; tone?: 'primary' | 'infra' | 'accent'; children: ReactNode }) {
  const { ref, scale } = useScale(width)
  const view = useInView<HTMLDivElement>()
  return (
    <div className={`mk-frame mk-tone-${tone}`} ref={view.ref} aria-hidden="true">
      <div className="mk-chrome"><span /><span /><span /><b>{path}</b></div>
      <div className="mk-viewport" ref={ref} style={{ height: height * scale }}>
        <div className={`mk-canvas${view.inView ? ' is-playing' : ''}`} style={{ width, height, transform: `scale(${scale})` }}>{children}</div>
      </div>
    </div>
  )
}

const delay = (index: number, step = 0.08): CSSProperties => ({ '--mk-delay': `${index * step}s` } as CSSProperties)

// --- ERP & CRM: quote pipeline -------------------------------------------------------------

const erpColumns = [
  [['RS-0603-4K7F', '24 000', 'hot'], ['CM-0805-226K', '15 000', ''], ['MC-G4-128QF', '3 200', ''], ['VR-1170T', '5 000', 'wait']],
  [['DX-2140S', '32 000', ''], ['PM-5433DC', '2 400', 'hot'], ['WL-S3-MOD2', '900', '']],
  [['RX-485LV8', '600', 'ok'], ['CN-HV72SO', '1 800', 'ok']],
  [['LG-1G08SOT', '6 000', 'ok'], ['FT-6344N', '7 500', 'ok'], ['MC-328QF', '350', 'ok']],
] as const

export function ErpMock({ ui }: { ui: Ui }) {
  const counts = [10, 69, 12, 7]
  return (
    <MockFrame path="erp / quotes" height={430}>
      <div className="mk-erp">
        <div className="mk-toolbar">
          <div className="mk-tabs">{ui.erp.modules.map((module, index) => <span className={index === 0 ? 'active' : ''} key={module}>{module}</span>)}</div>
          <div className="mk-input">⌕ {ui.erp.search}</div>
        </div>
        <div className="mk-kanban">
          {ui.erp.columns.map((column, columnIndex) => (
            <div className="mk-column" key={column}>
              <header><span>{column}</span><b>{counts[columnIndex]}</b></header>
              {erpColumns[columnIndex].map(([pn, qty, state], cardIndex) => (
                <div className={`mk-card mk-enter state-${state || 'none'}`} style={delay(columnIndex * 3 + cardIndex)} key={pn}>
                  <strong>{pn}</strong>
                  <span>{qty} {ui.erp.qty}</span>
                  <i />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mk-rail">
          {ui.erp.modules.map((module, index) => (
            <div key={module}><span>{module}</span><i style={{ '--fill': `${[82, 64, 91, 47][index]}%` } as CSSProperties} /></div>
          ))}
        </div>
      </div>
    </MockFrame>
  )
}

// --- B2B portal: offer comparison ------------------------------------------------------------

export function PortalMock({ ui }: { ui: Ui }) {
  const rows = [
    { supplier: ui.portal.suppliers[0], stock: '980 000', lead: `1–2 ${ui.portal.weeks}`, tiers: ['0,0060 × 2000', '0,0052 × 10000'], total: '12,00', best: true },
    { supplier: ui.portal.suppliers[3], stock: '64 300', lead: `2–3 ${ui.portal.weeks}`, tiers: ['0,0084 × 1000', '0,0079 × 5000'], total: '16,80' },
    { supplier: ui.portal.suppliers[1], stock: '15 000', lead: `3–4 ${ui.portal.weeks}`, tiers: ['0,0095 × 500'], total: '19,00' },
    { supplier: ui.portal.suppliers[2], stock: '2 800', lead: `4–5 ${ui.portal.weeks}`, tiers: ['0,0140 × 100'], total: '28,00' },
  ]
  return (
    <MockFrame path="portal / search" height={400}>
      <div className="mk-portal">
        <div className="mk-searchbar">
          <div className="mk-input wide">{ui.portal.search}: <b>DX-2140S</b></div>
          <div className="mk-input">{ui.portal.qty}: <b>2000</b></div>
          <div className="mk-button">{ui.portal.find}</div>
        </div>
        <div className="mk-table">
          <div className="mk-thead"><span>{ui.portal.supplier}</span><span>{ui.portal.stock}</span><span>{ui.portal.lead}</span><span>{ui.portal.price}</span><span>{ui.portal.total}</span><span /></div>
          {rows.map((row, index) => (
            <div className={`mk-row mk-enter${row.best ? ' best' : ''}`} style={delay(index, 0.12)} key={row.supplier}>
              <span><b>{row.supplier}</b><small>DX-2140S · SOT-23</small></span>
              <span>{row.stock}</span>
              <span>{row.lead}</span>
              <span className="mk-tiers">{row.tiers.map((tier) => <em key={tier}>{tier}</em>)}</span>
              <span><b>{row.total}</b></span>
              <span>{row.best ? <i className="mk-badge">{ui.portal.best}</i> : <i className="mk-cart" />}</span>
            </div>
          ))}
        </div>
      </div>
    </MockFrame>
  )
}

// --- Catalog: smart category search + product card --------------------------------------------

export function CatalogMock({ ui }: { ui: Ui }) {
  const items = [
    ['PL-4020-4R7M', 'Orion EC', 'IND PWR 4.7UH 3.2A 4020', 99],
    ['SD-4018-4R7N', 'Vega Passive', 'IND PWR 4.7UH 2.9A 4018', 98],
    ['MX-4030-4R7K', 'Altair Semi', 'IND PWR 4.7UH 3.6A 4030', 97],
    ['TP-4020-4R7M', 'Nova Parts', 'IND PWR 4.7UH 3.0A 4020', 95],
    ['CW-4040-4R7L', 'Stellar Components', 'IND PWR 4.7UH 4.1A 4040', 94],
  ] as const
  return (
    <MockFrame path="catalog / power-inductors" height={450}>
      <div className="mk-catalog">
        <div className="mk-smart">
          <div className="mk-input wide"><span className="mk-typed">{ui.catalog.query}</span></div>
          <span className="mk-arrow">→</span>
          <div className="mk-chip strong">{ui.catalog.section}</div>
          <div className="mk-chip">4.7 µH</div><div className="mk-chip">3 A</div><div className="mk-chip">4020</div>
          <small>{ui.catalog.positions}</small>
        </div>
        <div className="mk-catalog-body">
          <aside className="mk-filters">
            <header><span>{ui.catalog.filters}</span><i className="mk-button small">{ui.catalog.apply}</i></header>
            <label>Inductance</label>
            <div className="mk-range"><i /><b /><b /></div>
            <small>4.7 µH</small>
            <label>Tolerance</label>
            {[['±20%', '41 280', true], ['±10%', '28 610', false], ['±30%', '9 470', false], ['±5%', '3 150', false]].map(([label, count, checked]) => (
              <div className={`mk-check${checked ? ' on' : ''}`} key={String(label)}><i />{label}<em>{count}</em></div>
            ))}
            <label>Current</label>
            <div className="mk-chips"><span>2 A</span><span className="on">3 A</span><span>5 A</span></div>
          </aside>
          <div className="mk-list">
            <div className="mk-thead"><span>{ui.catalog.partNumber}</span><span>{ui.catalog.maker}</span><span /><span>{ui.catalog.relevance}</span></div>
            {items.map(([pn, maker, description, score], index) => (
              <div className="mk-row mk-enter" style={delay(index)} key={pn}>
                <span><i className="mk-thumb" /><b className="mk-link">{pn}</b></span><span>{maker}</span><span className="mk-dim">{description}</span><span><i className="mk-score">{score}</i></span>
              </div>
            ))}
          </div>
          <div className="mk-product mk-enter" style={delay(6)}>
            <small>Orion EC</small>
            <strong>PL-4020-4R7M</strong>
            <p>4.7 µH · 3.2 A · ±20% · 4020</p>
            <div className="mk-price"><b>$0,0480</b><span>{ui.catalog.median}</span></div>
            <svg viewBox="0 0 160 40" className="mk-spark"><polyline points="0,30 18,28 36,31 54,24 72,26 90,18 108,20 126,14 144,16 160,10" /><polyline className="area" points="0,30 18,28 36,31 54,24 72,26 90,18 108,20 126,14 144,16 160,10 160,40 0,40" /></svg>
            <small className="mk-dim">{ui.catalog.trend} · {ui.catalog.inStock}</small>
            <div className="mk-demand"><i>35</i>{ui.catalog.demand}</div>
            <div className="mk-actions"><span>{ui.catalog.datasheet}</span><span>{ui.catalog.analogs}</span></div>
            <div className="mk-button full">{ui.catalog.offers}</div>
          </div>
        </div>
      </div>
    </MockFrame>
  )
}

// --- AI search: mixed list in, grouped results out ------------------------------------------------

export function SearchMock({ ui }: { ui: Ui }) {
  const types = ['pn', 'desc', 'pn', 'desc', 'desc']
  const splitQty = (row: string) => {
    // The quantity is the trailing number ("1 000" style grouping allowed), so a package code such as "0805 2000" stays in the text.
    const match = row.match(/^(.*?)\s(\d{1,3}(?:\s\d{3})+|\d+)$/)
    return match ? [match[1], match[2]] : [row, '']
  }
  return (
    <MockFrame path="search / ai" height={450}>
      <div className="mk-search">
        <div className="mk-query">
          <header><b>{ui.search.title}</b><span>{ui.search.counter}</span></header>
          {ui.search.rows.map((row, index) => {
            const [text, qty] = splitQty(row)
            return (
              <div className="mk-line mk-enter" style={delay(index)} key={row}>
                <em>{index + 1}</em><i className={`dot ${types[index]}`} /><span>{text}</span>{qty && <b className="mk-qty">{qty}</b>}
              </div>
            )
          })}
          <div className="mk-file mk-enter" style={delay(6)}><span className="mk-doc">PDF</span><div><b>{ui.search.file}</b><small>{ui.search.fileResult}</small></div></div>
          <div className="mk-button full">{ui.search.find} ⌘↵</div>
        </div>
        <div className="mk-results">
          <div className="mk-group mk-enter" style={delay(3)}>
            <header><b>RX-485LV8</b><i className="mk-badge">{ui.search.exact}</i><em>600</em></header>
            <div className="mk-offer"><span>Transceiver · RS-485 · 8-SOIC</span><b>$0,212 × 600</b><i className="mk-tick" /></div>
          </div>
          <div className="mk-group mk-enter" style={delay(4)}>
            <header><b>4.7 kΩ 0603 5%</b><i className="mk-badge alt">{ui.search.byParams}</i><em>50</em></header>
            {[['RS-0603-4K7J', 'Orion EC', '$0,0021'], ['TF-0603-472J', 'Vega Passive', '$0,0034'], ['CR-0603-4K7-5', 'Altair Semi', '$0,0027']].map(([pn, maker, price], index) => (
              <div className="mk-offer" key={pn}><span><em>#{index + 1}</em> {pn} · {maker}</span><b>{price}</b>{index === 0 ? <i className="mk-tick" /> : <i className="mk-box" />}</div>
            ))}
          </div>
          <div className="mk-group mk-enter" style={delay(5)}>
            <header><b>DX-2140S</b><i className="mk-badge">{ui.search.exact}</i><em>150</em></header>
            <div className="mk-chips">{['DX-2170S', 'DX-1160S', 'DX-2156S', 'DX-2199L'].map((pn) => <span key={pn}>{pn} <small>{ui.search.analog}</small></span>)}</div>
          </div>
          <div className="mk-group muted mk-enter" style={delay(6)}>
            <header><b>“{ui.search.rows[4]}”</b><i className="mk-badge warn">{ui.search.unknown}</i></header>
          </div>
          <div className="mk-total mk-enter" style={delay(7)}><span>{ui.search.toCart}</span><b>$139,70</b></div>
        </div>
      </div>
    </MockFrame>
  )
}

// --- AI agent over MCP: the buying conversation --------------------------------------------------

export function AgentMock({ ui }: { ui: Ui }) {
  const a = ui.agent
  const steps: Array<{ who: 'user' | 'tool' | 'agent'; text: string; extra?: ReactNode }> = [
    { who: 'user', text: a.user1 },
    { who: 'tool', text: a.tool1 },
    { who: 'agent', text: a.agent1 },
    { who: 'user', text: a.user2 },
    { who: 'tool', text: a.tool2 },
    { who: 'agent', text: a.agent2, extra: <b className="mk-confirm">{a.confirm}</b> },
    { who: 'user', text: a.user3 },
    { who: 'tool', text: a.tool3 },
    { who: 'agent', text: a.agent3, extra: <span className="mk-attachment"><span className="mk-doc">PDF</span>{a.invoice}</span> },
  ]
  return (
    <MockFrame path="assistant · mcp" height={560} tone="accent">
      <div className="mk-agent">
        <header><i /><b>{a.title}</b><span>MCP</span></header>
        <div className="mk-chat">
          {steps.map((step, index) => (
            <div className={`mk-msg ${step.who} mk-enter`} style={delay(index, 0.55)} key={`${step.who}-${index}`}>
              {step.who === 'tool' ? <><i className="mk-spinner" /><code>{step.text}</code><em>✓</em></> : <p>{step.text}{step.extra}</p>}
            </div>
          ))}
        </div>
        <footer><span className="mk-typing"><i /><i /><i /></span>{a.typing}</footer>
      </div>
    </MockFrame>
  )
}

// --- Energy: grid map (CIM network model) ---------------------------------------------------------------

export function GridMock({ ui }: { ui: Ui }) {
  const subs = [[180, 110], [310, 70], [420, 150], [520, 90], [610, 190], [470, 260], [330, 230], [230, 300], [560, 330]]
  return (
    <MockFrame path="grid / network-scheme" height={420} tone="infra">
      <div className="mk-grid">
        <aside>
          <div className="mk-tabs"><span className="active">{ui.grid.editor}</span><span>{ui.grid.scheme}</span></div>
          {ui.grid.layers.map((layer, index) => <div className={`mk-layer${index === 0 ? ' on' : ''}`} key={layer}>{layer}<i>⌄</i></div>)}
          <div className="mk-legend">
            {ui.grid.legend.map((label, index) => <span key={label}><i className={`v${index}`} />{label}</span>)}
          </div>
        </aside>
        <div className="mk-map">
          <svg viewBox="0 0 760 420" preserveAspectRatio="xMidYMid slice">
            <path className="water" d="M520 420 C 560 330, 690 320, 760 250 L760 420 Z" />
            <path className="water" d="M0 380 C 90 350, 140 400, 210 420 L0 420 Z" />
            {Array.from({ length: 14 }, (_, index) => <path className="road" key={index} d={`M${index * 60 - 40} 0 L ${index * 60 + 80} 420`} />)}
            {Array.from({ length: 8 }, (_, index) => <path className="road" key={`h${index}`} d={`M0 ${index * 60 + 20} L 760 ${index * 60 - 20}`} />)}
            <polyline className="v0 mk-draw" points="120,40 180,110 310,70 420,150 520,90 700,60" />
            <polyline className="v1 mk-draw" points="180,110 230,300 330,230 420,150 470,260 560,330 610,190 520,90" />
            <path className="v2 mk-draw" d="M330 230 L290 280 L250 260 M470 260 L500 300 L530 285 M610 190 L660 230 L700 210 M230 300 L180 340" />
            {subs.map(([x, y], index) => (
              <g transform={`translate(${x} ${y})`} key={index}>
                <g className="sub mk-enter" style={delay(index, 0.1)}>
                  <rect x="-7" y="-7" width="14" height="14" rx="3" />
                  <text x="11" y="4">TS-{index + 3}</text>
                </g>
              </g>
            ))}
          </svg>
          <div className="mk-mapsearch">⌕ {ui.grid.search}</div>
          <div className="mk-popup mk-enter" style={delay(8)}>
            <b>{ui.grid.substation}</b>
            <svg viewBox="0 0 150 90" className="mk-sld">
              <line x1="10" y1="12" x2="140" y2="12" />
              <line x1="45" y1="12" x2="45" y2="30" /><line x1="105" y1="12" x2="105" y2="30" />
              <circle cx="45" cy="40" r="10" /><circle cx="45" cy="52" r="10" />
              <circle cx="105" cy="40" r="10" /><circle cx="105" cy="52" r="10" />
              <line x1="45" y1="62" x2="45" y2="78" /><line x1="105" y1="62" x2="105" y2="78" />
              <line x1="20" y1="78" x2="130" y2="78" />
              <text x="58" y="48">T-1</text><text x="118" y="48">T-2</text>
            </svg>
          </div>
        </div>
      </div>
    </MockFrame>
  )
}

// --- Energy: LV panel configurator ---------------------------------------------------------------------------

export function PanelMock({ ui }: { ui: Ui }) {
  const units = ['ACB', 'BUS TIE', 'MCCB', 'DOL', 'REV', 'S/Y', 'VSD', 'PFC']
  const feeders = ['ACB', 'DOL', 'REV', 'S/Y', 'VSD', 'DOL']
  return (
    <MockFrame path="configurator / lv-panel" height={420} tone="infra">
      <div className="mk-panel">
        <div className="mk-units">
          <small>{ui.panel.units}</small>
          {units.map((unit, index) => (
            <div className="mk-unit mk-enter" style={delay(index, 0.05)} key={unit}>
              <svg viewBox="0 0 20 34"><line x1="10" y1="0" x2="10" y2="12" /><line x1="10" y1="12" x2="16" y2="20" /><line x1="10" y1="22" x2="10" y2="34" /></svg>
              <span>{unit}</span>
            </div>
          ))}
        </div>
        <div className="mk-panel-body">
          <div className="mk-diagram">
            <svg viewBox="0 0 460 200">
              <line className="bus mk-draw" x1="10" y1="16" x2="450" y2="16" />
              {feeders.map((feeder, index) => {
                const x = 45 + index * 72
                return (
                  <g className="mk-enter" style={delay(index + 3, 0.1)} key={index}>
                    <line x1={x} y1="16" x2={x} y2="46" />
                    <line x1={x} y1="46" x2={x + 9} y2="60" />
                    <line x1={x} y1="64" x2={x} y2="100" />
                    <circle cx={x} cy="110" r="9" />
                    <text x={x} y="113" textAnchor="middle">M</text>
                    <text className="label" x={x} y="140" textAnchor="middle">{feeder}</text>
                    <rect x={x - 30} y="150" width="60" height="42" rx="4" />
                    <text className="cell" x={x} y="166" textAnchor="middle">{ui.panel.feeder} {index + 1}</text>
                    <text className="cell dim" x={x} y="182" textAnchor="middle">{[630, 22, 15, 30, 55, 11][index]} {index === 0 ? 'A' : 'kW'}</text>
                  </g>
                )
              })}
            </svg>
          </div>
          <aside className="mk-settings">
            <b>{ui.panel.settings}</b>
            {ui.panel.params.map(([label, value]) => <div key={label}><span>{label}</span><em>{value}</em></div>)}
          </aside>
          <div className="mk-front mk-enter" style={delay(8)}>
            <small>{ui.panel.front} · 1 : 50</small>
            <div className="mk-cabinets">
              {[0, 1, 2, 3].map((cabinet) => (
                <div className="mk-cabinet" key={cabinet}>{Array.from({ length: cabinet === 0 ? 3 : 6 }, (_, cell) => <i key={cell} className={cabinet === 0 && cell === 0 ? 'big' : ''} />)}</div>
              ))}
            </div>
            <div className="mk-ruler"><span>2400 mm</span></div>
          </div>
          <div className="mk-export mk-enter" style={delay(9)}>
            <small>{ui.panel.export}</small>
            {['PDF', 'XLS', 'DXF'].map((format) => <span key={format}>{format}</span>)}
          </div>
        </div>
      </div>
    </MockFrame>
  )
}

// --- Energy: asset passport on the map ---------------------------------------------------------------------

export function TowerMock({ ui }: { ui: Ui }) {
  const t = ui.tower
  const towers = [[90, 60], [150, 120], [205, 175], [265, 235], [320, 290], [380, 345], [430, 110], [470, 170], [520, 230], [575, 290], [630, 350], [120, 300], [200, 330], [290, 360]]
  return (
    <MockFrame path="grid / assets / tower-14" height={420} tone="infra">
      <div className="mk-tower">
        <div className="mk-map">
          <svg viewBox="0 0 760 420" preserveAspectRatio="xMidYMid slice">
            <path className="water" d="M600 0 C 640 80, 720 110, 760 90 L760 0 Z" />
            {Array.from({ length: 12 }, (_, index) => <path className="road" key={index} d={`M${index * 70 - 60} 0 L ${index * 70 + 60} 420`} />)}
            <polyline className="v1 mk-draw" points="90,60 150,120 205,175 265,235 320,290 380,345" />
            <polyline className="v0 mk-draw" points="430,110 470,170 520,230 575,290 630,350" />
            <polyline className="v2 mk-draw" points="120,300 200,330 290,360" />
            {towers.map(([x, y], index) => (
              <g transform={`translate(${x} ${y})`} key={index}>
                <g className={`tower mk-enter${index === 8 ? ' selected' : ''}`} style={delay(index, 0.05)}>
                  <path d="M0 -8 L0 8 M-6 -4 L6 -4 M-4 3 L4 3" />
                  {index === 8 && <circle r="14" />}
                </g>
              </g>
            ))}
          </svg>
          <div className="mk-mapsearch">⌕ {ui.grid.search}</div>
        </div>
        <div className="mk-passport mk-enter" style={delay(4)}>
          <header><b>{t.title}</b><span>ID 0147</span></header>
          <div className="mk-photo"><svg viewBox="0 0 120 80"><path d="M60 8 L60 76 M44 22 L76 22 M48 36 L72 36" /><path className="wire" d="M0 20 Q 30 26 44 22 M76 22 Q 100 18 120 24 M0 34 Q 30 40 48 36 M72 36 Q 100 32 120 38" /></svg><em>{t.photo}</em></div>
          {t.rows.map(([label, value]) => <div className="mk-kv" key={label}><span>{label}</span><b>{value}</b></div>)}
          <small>{t.history}</small>
          {t.events.map(([date, text]) => <div className="mk-event" key={date}><em>{date}</em>{text}</div>)}
          <div className="mk-button full">{t.add}</div>
        </div>
      </div>
    </MockFrame>
  )
}

// --- Energy: defect ranking + repair schedule --------------------------------------------------------------------

export function MonitorMock({ ui }: { ui: Ui }) {
  const m = ui.monitor
  const spans = [[0, 2], [2, 2], [4, 2], [1, 5]]
  return (
    <MockFrame path="grid / monitoring" height={450} tone="infra">
      <div className="mk-monitor">
        <header><b>{m.title}</b><span className="mk-chip">{m.resources}: {m.crew}</span></header>
        <div className="mk-rank">
          <div className="mk-thead"><span>{m.cols[0]}</span><span>{m.cols[1]}</span><span>{m.cols[2]}</span><span>{m.cols[3]}</span></div>
          {m.rows.map(([asset, defect, score, repair], index) => (
            <div className="mk-row mk-enter" style={delay(index, 0.12)} key={asset}>
              <span><b>{asset}</b></span><span className="mk-dim">{defect}</span>
              <span className="mk-crit"><i className={score > 85 ? 'high' : score > 60 ? 'mid' : 'low'} style={{ '--w': `${score}%` } as CSSProperties} /><em>{score}</em></span>
              <span>{repair}</span>
            </div>
          ))}
        </div>
        <div className="mk-gantt">
          <small>{m.schedule}</small>
          <div className="mk-weeks">{m.weeks.map((week) => <span key={week}>{week}</span>)}</div>
          {m.rows.map(([asset, , score], index) => (
            <div className="mk-lane" key={asset}>
              <span>{asset}</span>
              <div><i className={`mk-enter ${score > 85 ? 'high' : score > 60 ? 'mid' : 'low'}`} style={{ ...delay(index + 5, 0.1), left: `${(spans[index][0] / 6) * 100}%`, width: `${(spans[index][1] / 6) * 100}%` } as CSSProperties} /></div>
            </div>
          ))}
        </div>
      </div>
    </MockFrame>
  )
}

// --- Energy: field work orders ---------------------------------------------------------------------------------------

export function WorksMock({ ui }: { ui: Ui }) {
  const w = ui.works
  return (
    <MockFrame path="grid / work-orders" height={420} tone="infra">
      <div className="mk-works">
        <div className="mk-kanban three">
          {w.columns.map((column, columnIndex) => (
            <div className="mk-column" key={column}>
              <header><span>{column}</span><b>{w.items.filter((item) => item[2] === columnIndex).length}</b></header>
              {w.items.filter((item) => item[2] === columnIndex).map(([title, meta], index) => (
                <div className={`mk-card mk-enter state-${columnIndex === 2 ? 'ok' : columnIndex === 1 ? 'wait' : 'none'}`} style={delay(columnIndex * 2 + index)} key={title}>
                  <strong className="plain">{title}</strong><span>{meta}</span><i />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mk-crew mk-enter" style={delay(7)}>
          <svg viewBox="0 0 220 120"><path className="road" d="M0 90 L220 30 M40 0 L120 120 M150 0 L200 120" /><polyline className="v1" points="10,100 70,70 130,60 200,20" /><g transform="translate(130 60)"><circle className="pulse" r="14" /><circle className="dot" r="5" /></g></svg>
          <div><b>{w.crew}</b><span>{w.crewStatus}</span></div>
          <em className="mk-chip">{w.connection}</em>
        </div>
      </div>
    </MockFrame>
  )
}

// --- Energy: requirements form with compatibility checks -------------------------------------------------------

export function SpecMock({ ui }: { ui: Ui }) {
  const s = ui.spec
  return (
    <MockFrame path="configurator / requirements" height={420} tone="infra">
      <div className="mk-spec">
        <header><b>{s.title}</b></header>
        <div className="mk-fields">
          {s.fields.map(([label, value], index) => (
            <label className={`mk-enter${index === 5 ? ' flagged' : ''}`} style={delay(index, 0.07)} key={label}>
              <span>{label}</span><em>{value}<i>⌄</i></em>
            </label>
          ))}
        </div>
        <div className="mk-warning mk-enter" style={delay(8)}><i>!</i><span>{s.warning}</span><b className="mk-button small">{s.fix}</b></div>
        <div className="mk-recalc mk-enter" style={delay(11)}><i>✓</i>{s.recalc}</div>
        <div className="mk-button full mk-enter" style={delay(12)}>{s.build}</div>
      </div>
    </MockFrame>
  )
}

// --- Energy: document packages per department --------------------------------------------------------------------

export function DocsMock({ ui }: { ui: Ui }) {
  const d = ui.docs
  const formatOf = (name: string, index: number) => (/(схема|diagram|вид|view|чертеж|drawing|schema|ansicht|zeichnung|esquema|vista|plano|单线图|外观|装配图|แผนภาพ|ภาพรวม|แบบประกอบ)/i.test(name) ? (index % 2 ? 'DXF' : 'PDF') : /(спецификац|specification|трудо|labor|spezifikation|arbeitsaufwand|especificaci|mano de obra|规格|人工|สเปก|ค่าแรง)/i.test(name) ? 'XLS' : 'PDF')
  return (
    <MockFrame path="configurator / documents" height={420} tone="infra">
      <div className="mk-docs">
        {d.departments.map((department, column) => (
          <div className="mk-dept" key={department}>
            <header><b>{department}</b><span>{d.items[column].length}</span></header>
            {d.items[column].map((item, index) => {
              const format = formatOf(item, index)
              return (
                <div className="mk-docrow mk-enter" style={delay(column * 3 + index, 0.06)} key={item}>
                  <span className={`mk-fmt f-${format.toLowerCase()}`}>{format}</span>
                  <span className="name">{item}</span>
                  <em className={index === 1 && column === 0 ? 'wip' : ''}>{index === 1 && column === 0 ? d.editing : d.approved}</em>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </MockFrame>
  )
}

// --- Energy: estimate by cabinet ---------------------------------------------------------------------------------------

export function BomMock({ ui }: { ui: Ui }) {
  const b = ui.bom
  return (
    <MockFrame path="configurator / estimate.xls" height={420} tone="infra">
      <div className="mk-bom">
        <header><b>{b.title}</b><span className="mk-chip">{b.maker}</span></header>
        <div className="mk-sheet">
          <div className="mk-thead"><span>{b.cols[0]}</span><span>{b.cols[1]}</span><span>{b.cols[2]}</span><span>{b.cols[3]}</span></div>
          {b.rows.map(([cabinet, module, component, price], index) => (
            <div className="mk-row mk-enter" style={delay(index, 0.1)} key={cabinet}>
              <span><b>{cabinet}</b></span><span className="mk-dim">{module}</span><span>{component}</span><span className="num">{price}</span>
            </div>
          ))}
          <div className="mk-row total mk-enter" style={delay(6)}><span>{b.total}</span><span /><span /><span className="num">{b.totalValue}</span></div>
        </div>
        <div className="mk-export inline mk-enter" style={delay(7)}>{['PDF', 'XLS', 'DXF'].map((format) => <span key={format}>{format}</span>)}</div>
      </div>
    </MockFrame>
  )
}
